import {
  createFileRoute,
  Outlet,
  useNavigate,
  useLocation,
  Link,
} from '@tanstack/react-router';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import {
  FiDollarSign,
  FiSettings,
  FiLogOut,
  FiMessageCircle,
  FiCalendar,
  FiBell,
  FiBookOpen,
} from 'react-icons/fi';
import { apiJson, ApiError } from '@/lib/api';
import { Paywall } from '@/components';
import { WhatsNewModal } from '@/components/WhatsNewModal';
import { HouseholdSwitcher } from '@/components/HouseholdSwitcher';
import { HouseholdProvider } from '@/lib/household-context';
import { identifyUser, resetUser } from '@/lib/analytics';
import type {
  UserResponse,
  HouseholdResponse,
  InviteListResponse,
  ConversationsResponse,
  UnreadCountResponse,
} from '@/lib/types';
import {
  getCoparentInviteSkipped,
  areInvestmentStepsDone,
  getFirstIncompleteInvestmentStep,
} from '@/lib/storage';
import shipSvg from '@/assets/ship.svg';

// Sidebar component for desktop
function Sidebar({
  currentPath,
  user,
  unreadCount,
  notificationUnreadCount,
}: {
  currentPath: string;
  user?: UserResponse;
  unreadCount: number;
  notificationUnreadCount: number;
}) {
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const navItems = [
    { to: '/app/expenses', label: 'Expenses', icon: FiDollarSign, badge: 0 },
    { to: '/app/calendar', label: 'Calendar', icon: FiCalendar, badge: 0 },
    { to: '/app/journal', label: 'Journal', icon: FiBookOpen, badge: 0 },
    {
      to: '/app/chat',
      label: 'Messages',
      icon: FiMessageCircle,
      badge: unreadCount,
    },
    {
      to: '/app/notifications',
      label: 'Notifications',
      icon: FiBell,
      badge: notificationUnreadCount,
    },
    { to: '/app/settings', label: 'Settings', icon: FiSettings, badge: 0 },
  ] as const;

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      resetUser();
      await signOut();
      navigate({ to: '/', search: { hero: undefined } });
    }
  };

  return (
    <aside className='hidden lg:flex flex-col w-64 shrink-0 bg-white border-r border-gray-200 h-full min-h-0'>
      {/* Logo */}
      <div className='px-6 py-4 border-b border-gray-200 flex items-center'>
        <Link
          to='/app/expenses'
          className='flex items-center gap-2 text-primary text-xl font-light'
          style={{
            fontFamily:
              "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <img src={shipSvg} alt='' className='w-5 h-5 mb-2' />
          Harbor
        </Link>
      </div>

      {/* Navigation */}
      <nav className='flex-1 min-h-0 overflow-y-auto p-4'>
        <ul className='space-y-1'>
          {navItems.map((item) => {
            const isActive = currentPath.startsWith(item.to);
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  style={{
                    fontFamily:
                      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  <Icon className='w-5 h-5' />
                  <span className='flex-1'>{item.label}</span>
                  {item.badge > 0 && (
                    <span
                      className={`px-1.5 min-w-[20px] h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                        isActive
                          ? 'bg-white text-primary'
                          : 'bg-primary text-white'
                      }`}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User section */}
      <div className='p-4 border-t border-gray-100'>
        {user && (
          <div className='flex items-center gap-3 px-4 py-3'>
            <div className='w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm'>
              {(user.first_name?.[0] ?? '').toUpperCase()}
              {(user.last_name?.[0] ?? '').toUpperCase()}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='font-semibold text-gray-900 truncate text-sm'>
                {user.first_name} {user.last_name}
              </p>
              <p className='text-xs text-gray-500 truncate'>{user.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={handleSignOut}
          className='w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors font-semibold'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <FiLogOut className='w-5 h-5' />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function AppLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Fetch user data
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Fetch invites
  const invitesQuery = useQuery({
    queryKey: ['invites'],
    queryFn: async () => {
      try {
        return await apiJson<InviteListResponse>(getToken, '/v1/invites');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return { received: [], sent: [] } as InviteListResponse;
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Fetch conversations for unread count
  const conversationsQuery = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      try {
        return await apiJson<ConversationsResponse>(
          getToken,
          '/v1/conversations'
        );
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return {
            main_chat: {
              last_message_content: null,
              last_message_sender_first_name: null,
              last_message_timestamp: null,
              unread_count: 0,
            },
            custom_chats: [],
            unresolved_disputes: [],
            resolved_disputes: [],
          } as ConversationsResponse;
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
    refetchInterval: 30000, // Poll every 30 seconds for unread updates
  });

  // Fetch notifications unread count for badge
  const notificationsUnreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      try {
        return await apiJson<UnreadCountResponse>(
          getToken,
          '/v1/notifications/unread-count'
        );
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return { unread_count: 0 } as UnreadCountResponse;
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
    refetchInterval: 30000, // Poll every 30 seconds for unread updates
  });

  // Handle subscription purchase success
  const handlePurchaseSuccess = useCallback(async () => {
    // Refresh user data to get updated subscription status
    await queryClient.invalidateQueries({ queryKey: ['user'] });
  }, [queryClient]);

  // Identify user in Amplitude when user data is available
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userQuery.data) {
      return;
    }

    const user = userQuery.data;
    identifyUser({
      id: user.id,
      created_at: user.created_at,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone_number: user.phone_number,
      date_of_birth: user.date_of_birth,
      subscribed: user.subscribed,
    });
  }, [isLoaded, isSignedIn, userQuery.data]);

  // Redirect to landing page if not signed in
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/', search: { hero: undefined } });
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Email-only accounts (professionals): keep them out of coparent paywall / onboarding
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userQuery.isSuccess || !userQuery.data) {
      return;
    }
    if (userQuery.data.phone_number?.trim()) {
      return;
    }
    navigate({ to: '/pro' });
  }, [isLoaded, isSignedIn, userQuery.isSuccess, userQuery.data, navigate]);

  // Handle onboarding redirects
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    const ready =
      userQuery.isFetched &&
      householdsQuery.isFetched &&
      invitesQuery.isFetched;

    if (!ready) return;

    if (!userQuery.data?.phone_number?.trim()) {
      return;
    }

    const user = userQuery.data;
    const households = householdsQuery.data ?? [];
    const coparentInviteSkipped = getCoparentInviteSkipped();

    const firstHousehold = households[0];
    const householdMembersCount = firstHousehold?.members?.length ?? 0;
    const hasAnyChild = (firstHousehold?.beneficiaries ?? []).some(
      (b) => b.type === 'CHILD'
    );

    const isOnboarding = location.pathname.includes('/onboarding');

    // Step 1: Check investment steps (engagement-only screens)
    if (!areInvestmentStepsDone()) {
      const investmentTarget = getFirstIncompleteInvestmentStep();
      if (investmentTarget && !isOnboarding) {
        navigate({ to: investmentTarget });
        return;
      }
      // If already on an onboarding page, let them continue
      if (isOnboarding) return;
    }

    // Step 2: Check functional onboarding requirements
    const needsName =
      !user?.first_name?.trim() ||
      !user?.last_name?.trim() ||
      !user?.email?.trim();

    // Stay on invite until Continues/Skips — don't treat a sent invite as
    // completing the step (same race as mobile: refetch would skip the
    // confirmation UI).
    const needsCoparentInvite =
      !coparentInviteSkipped &&
      (!firstHousehold || householdMembersCount < 2);

    const needsChildren = Boolean(firstHousehold) && !hasAnyChild;

    // Determine onboarding target
    const onboardingTarget = needsName
      ? '/app/onboarding/profile'
      : needsCoparentInvite
      ? '/app/onboarding/invite'
      : needsChildren
      ? '/app/onboarding/children'
      : null;

    // Redirect to onboarding if needed
    if (onboardingTarget && !isOnboarding) {
      navigate({ to: onboardingTarget });
    }
    // Redirect away from onboarding if complete
    else if (!onboardingTarget && isOnboarding) {
      navigate({ to: '/app/expenses' });
    }
  }, [
    isLoaded,
    isSignedIn,
    userQuery.isFetched,
    userQuery.data,
    householdsQuery.isFetched,
    householdsQuery.data,
    invitesQuery.isFetched,
    invitesQuery.data,
    location.pathname,
    navigate,
  ]);

  // Loading state
  if (!isLoaded) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  // Not signed in - will redirect
  if (!isSignedIn) {
    return null;
  }

  // Loading data
  const ready =
    userQuery.isFetched && householdsQuery.isFetched && invitesQuery.isFetched;

  if (!ready) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  // Error state
  const hasError =
    userQuery.isError || householdsQuery.isError || invitesQuery.isError;

  if (hasError) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center p-6'>
        <div className='text-center max-w-md'>
          <h1 className='text-xl font-bold text-gray-900 mb-2'>
            Connection Error
          </h1>
          <p className='text-gray-600 mb-6'>
            Unable to connect to the server. Please check your connection and
            try again.
          </p>
          <button
            onClick={() => {
              userQuery.refetch();
              householdsQuery.refetch();
              invitesQuery.refetch();
            }}
            className='px-6 py-3 bg-primary text-white rounded-2xl font-semibold hover:bg-primary-hover transition-colors'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isOnboarding = location.pathname.includes('/onboarding');

  // Don't show sidebar during onboarding
  if (isOnboarding) {
    return (
      <HouseholdProvider households={householdsQuery.data ?? []}>
        <Outlet />
      </HouseholdProvider>
    );
  }

  // Check if user needs to subscribe (after onboarding is complete)
  const user = userQuery.data;
  const needsSubscription = user && !user.subscribed;

  // Show paywall if user is not subscribed
  if (needsSubscription && user.id) {
    return (
      <Paywall
        getToken={getToken}
        userId={user.id}
        onPurchaseSuccess={handlePurchaseSuccess}
        userEmail={user.email}
      />
    );
  }

  // Calculate total unread messages
  const totalUnread =
    (conversationsQuery.data?.main_chat?.unread_count ?? 0) +
    (conversationsQuery.data?.unresolved_disputes ?? []).reduce(
      (sum, d) => sum + d.unread_count,
      0
    );

  // Notification unread count
  const notificationUnreadCount =
    notificationsUnreadQuery.data?.unread_count ?? 0;

  // Mobile tab bar items
  const mobileNavItems = [
    { to: '/app/expenses', label: 'Expenses', icon: FiDollarSign, badge: 0 },
    { to: '/app/calendar', label: 'Calendar', icon: FiCalendar, badge: 0 },
    { to: '/app/journal', label: 'Journal', icon: FiBookOpen, badge: 0 },
    {
      to: '/app/chat',
      label: 'Messages',
      icon: FiMessageCircle,
      badge: totalUnread,
    },
    {
      to: '/app/notifications',
      label: 'Alerts',
      icon: FiBell,
      badge: notificationUnreadCount,
    },
    { to: '/app/settings', label: 'Settings', icon: FiSettings, badge: 0 },
  ] as const;

  return (
    <HouseholdProvider households={householdsQuery.data ?? []}>
    <div className='flex min-h-screen lg:min-h-0 lg:h-screen lg:max-h-screen lg:overflow-hidden'>
      <Sidebar
        currentPath={location.pathname}
        user={userQuery.data}
        unreadCount={totalUnread}
        notificationUnreadCount={notificationUnreadCount}
      />
      <main className='flex-1 min-w-0 min-h-0 lg:overflow-auto pb-16 lg:pb-0'>
        <HouseholdSwitcher currentUserId={userQuery.data?.id ?? null} />
        <Outlet />
      </main>

      {/* Mobile Bottom Tab Bar */}
      <nav className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-50 safe-area-inset-bottom'>
        <div className='flex items-center justify-around h-16'>
          {mobileNavItems.map((item) => {
            const isActive = location.pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                  isActive ? 'text-primary' : 'text-gray-500'
                }`}
              >
                <div className='relative'>
                  <Icon className='w-6 h-6' />
                  {item.badge > 0 && (
                    <span className='absolute -top-1.5 -right-1.5 px-1 min-w-[16px] h-4 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center'>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className='text-[10px] font-semibold mt-1'>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      <WhatsNewModal />
    </div>
    </HouseholdProvider>
  );
}

export const Route = createFileRoute('/app')({
  component: AppLayout,
});
